/**
 * Deterministically pick one option from a list using a stable seed: the same
 * seed always yields the same choice. Paired with `useStableSeed`, this rotates
 * between equivalent copy variants without the copy flipping on re-render.
 */
export function pickStable<T>(options: readonly T[], seed: number): T {
  if (options.length === 0) {
    throw new Error('pickStable requires at least one option')
  }
  const index = Math.abs(Math.trunc(seed)) % options.length
  const choice = options[index]
  if (choice === undefined) {
    throw new Error('pickStable computed an out-of-range index')
  }
  return choice
}

// --- Week Streak ---------------------------------------------------------

export type StreakCopyState =
  | 'noStreak'
  | 'atRisk'
  | 'onTrack'
  | 'hit'
  | 'ahead'

export type StreakCopy =
  | { kind: 'plain'; text: string }
  | { kind: 'link'; text: string }
  | { kind: 'prefixLink'; prefix: string; link: string }

/**
 * Copy variants per state. Each entry states how much of its line opens the
 * composer: all of it (`link`), the `link` field only (`prefixLink`), or none
 * of it (`plain`). Punctuation between the two halves of a `prefixLink` is
 * ordinary copy inside `prefix`, so changing it stays a copy edit.
 */
const STREAK_COPY: Record<StreakCopyState, readonly StreakCopy[]> = {
  noStreak: [
    { kind: 'link', text: 'Start your streak today' },
    { kind: 'link', text: 'Post today, start a streak' },
  ],
  atRisk: [
    { kind: 'prefixLink', prefix: 'At risk ·', link: 'start a post' },
    { kind: 'prefixLink', prefix: 'At risk ·', link: 'just one post!' },
  ],
  onTrack: [
    { kind: 'plain', text: 'On track, nice!' },
    { kind: 'plain', text: 'On track and going strong' },
  ],
  hit: [
    { kind: 'plain', text: 'Streak hit, keep going!' },
    { kind: 'plain', text: 'Streak hit, well done!' },
  ],
  ahead: [
    { kind: 'plain', text: 'Well ahead of schedule' },
    { kind: 'plain', text: 'Ahead for next week' },
  ],
}

/**
 * Monday-only overrides. Monday is when people log in and plan the week, so
 * the at-risk line leads with the fresh start instead of the warning. The
 * state itself is unchanged — the tile still reads at risk — only the wording
 * moves. States absent here use their `STREAK_COPY` variants every day.
 */
const STREAK_MONDAY_COPY: Partial<
  Record<StreakCopyState, readonly StreakCopy[]>
> = {
  atRisk: [
    { kind: 'prefixLink', prefix: 'At risk · New week,', link: 'new post!' },
  ],
}

/**
 * Short, single-line fallbacks for the plain (non-CTA) streak states. Home
 * shows these instead of the full rotating copy when the stat column is too
 * narrow for the full line (which would otherwise wrap to two lines). The CTA
 * states (`noStreak`/`atRisk`) return `null` and keep their full link copy.
 */
const STREAK_SHORT_TEXT: Partial<Record<StreakCopyState, string>> = {
  onTrack: 'On track',
  hit: 'Streak hit',
  ahead: 'Ahead',
}

export function getStreakShortText(state: StreakCopyState): string | null {
  return STREAK_SHORT_TEXT[state] ?? null
}

/**
 * Map raw streak data to a copy state.
 *
 * `periodsAhead` describes *next* week and `status` describes *this* one. The
 * two are independent, so a post queued for next week with nothing sent or
 * scheduled for the rest of this week is both `AtRisk` and one period ahead.
 * Ahead therefore only qualifies a positive status. The server draws the same
 * line: `mapToUserStreakState` appends `AndAhead` to `OnTrack` and to `Hit`,
 * and to nothing else. Keep to that set. A wider rule makes Home contradict
 * the sidebar and its own tile, because `getStreakTileClass` and
 * `getStreakFill` both read the raw `status`.
 *
 * A `Started` status with an existing count counts as On track. `Started` with
 * no count, `Missed`, and missing data are all "no streak".
 *
 * Do not add rules here. `Streak.userStreakState` is the server's answer to
 * this exact question, the streak query already selects it, and the schema
 * calls the logic behind it opaque on purpose. Every rule below is a second
 * copy of that logic, and it drifts on its own. Put new state distinctions in
 * `mapToUserStreakState` and read the field, instead of deriving state again
 * from `status`, `count`, and `periodsAhead`.
 */
export function resolveStreakState({
  status,
  count,
  periodsAhead,
}: {
  status: string | undefined
  count: number
  periodsAhead: number
}): StreakCopyState {
  const canBeAhead = status === 'OnTrack' || status === 'Hit'
  if (periodsAhead >= 1 && canBeAhead) return 'ahead'
  if (status === 'AtRisk') return 'atRisk'
  if (status === 'Hit') return 'hit'
  if (status === 'OnTrack') return 'onTrack'
  if (status === 'Started' && count > 0) return 'onTrack'
  return 'noStreak'
}

/**
 * Resolve the streak subtext, rotating between variants with `seed`.
 *
 * `isMonday` opts into the Monday-only wording for states that define it, and
 * is the caller's to resolve so the copy stays a pure function of its inputs —
 * Home passes the account's timezone, not the browser's.
 */
export function getStreakCopy(
  state: StreakCopyState,
  seed: number,
  { isMonday = false }: { isMonday?: boolean } = {},
): StreakCopy {
  const mondayVariants = isMonday ? STREAK_MONDAY_COPY[state] : undefined
  return pickStable(mondayVariants ?? STREAK_COPY[state], seed)
}

// --- Posting Goals -------------------------------------------------------

/**
 * Subtext for the posting-goals tile. Ordered buckets (first match wins):
 * no goals set → kept current copy; none met; all met; one short; ≥80% →
 * almost there; 45–55% → halfway there; otherwise the plain count. Thresholds
 * are tunable.
 */
export function getPostingGoalsCopy(completed: number, total: number): string {
  if (total === 0) return 'No goals yet'
  if (completed === 0) return 'No goals met yet'
  if (completed === total) return `${completed} of ${total}, nice work!`
  if (completed === total - 1) return `${completed} of ${total}, so close!`

  const ratio = completed / total
  if (ratio >= 0.8) return `${completed} of ${total}, almost there`
  if (ratio >= 0.45 && ratio <= 0.55) {
    return `${completed} of ${total}, halfway there`
  }
  return `${completed} of ${total} goals met`
}

// --- Comment Score -------------------------------------------------------

const COMMENT_SCORE_FLAT_COPY = [
  'Same as last week',
  'Holding steady',
  'Nice and consistent',
]

/** Rounded percentage delta between the two periods. `sign === null` is flat. */
export function commentScoreTrend(
  current: number,
  previous: number,
): { sign: '+' | '-' | null; percent: number } {
  if (previous === 0) {
    if (current === 0) return { sign: null, percent: 0 }
    return { sign: '+', percent: 100 }
  }
  const delta = ((current - previous) / previous) * 100
  const rounded = Math.round(Math.abs(delta))
  if (rounded === 0) return { sign: null, percent: 0 }
  return { sign: delta > 0 ? '+' : '-', percent: rounded }
}

export type CommentScoreCopy =
  | { kind: 'text'; text: string }
  | { kind: 'trend'; sign: '+' | '-'; percent: number; text: string }

/**
 * Comment-score subtext. No data → "No activity yet"; a flat delta rotates
 * between equivalent copy via `seed`; otherwise a "±X% from last week" trend
 * the tile renders with a direction icon.
 */
export function getCommentScoreCopy({
  hasData,
  current,
  previous,
  seed,
}: {
  hasData: boolean
  current: number
  previous: number
  seed: number
}): CommentScoreCopy {
  if (!hasData) {
    return { kind: 'text', text: 'No activity yet' }
  }
  const { sign, percent } = commentScoreTrend(current, previous)
  if (sign === null) {
    return { kind: 'text', text: pickStable(COMMENT_SCORE_FLAT_COPY, seed) }
  }
  return {
    kind: 'trend',
    sign,
    percent,
    text: `${percent}% from last week`,
  }
}
